import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { Observable, Subject, combineLatest } from 'rxjs'
import { map, takeUntil, takeWhile } from 'rxjs/operators'

import { DateService } from '@lemon/core/service/date.service'

import {
  SOPIMUS_LIITETYYPIT,
  SopimuksenTemplateUrlRequest,
  SopimuksenTemplateUrlResponse,
  Sopimus,
  SopimusLiiteHakutulos,
  SopimusLiiteLatausPyynto,
  SopimusLiiteLatausVastaus,
  SopimusLiiteMap,
  SopimusLiitetyyppi,
  SopimusLiitteetHakuVastaus,
} from '@lemon/core-firebase/model/sopimus'
import { TuettuKieli } from '@lemon/core/model/common'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { YllapitoUriService } from '../../_jaettu-lemonator/service/yllapito-uri.service'
import { SopimuksenJulkaiseminenDialog, SopimuksenJulkaiseminenDialogData } from './sopimuksen-julkaiseminen.dialog'

type LiiteStatus = 'idle' | 'selected' | 'uploading' | 'valid' | 'error'

interface SopimuksenVersiorivi {
  avain: string
  aika: string
  kirjanpitaja: string
  tekstifi: string | null
  tekstien: string | null
}

interface SopimusLomake {
  textfi: string
  texten: string
}

@Component({
  templateUrl: './sopimukset.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SopimuksetComponent implements OnInit, OnDestroy {
  private _ngUnsubsribe: Subject<void> = new Subject<void>()
  private _nykyinenSopimusAvain: string | null = null
  private _blobUrlFi: string | null = null
  private _blobUrlEn: string | null = null
  private _liiteBlobUrls: SopimusLiiteMap<{ fi: string; en: string }> = {}
  readonly cachedBase64Map = signal<SopimusLiiteMap<{ fi?: string; en?: string }>>({})

  sopimustenVersiotObservable: Observable<SopimuksenVersiorivi[]>
  usableVariablesSuomiObservable: Observable<string[]>
  usableVariablesEnglishObservable: Observable<string[]>
  julkaiseEnabledObservable: Observable<boolean>

  readonly templateUrlFi = signal<SafeResourceUrl | null>(null)
  readonly templateUrlEn = signal<SafeResourceUrl | null>(null)
  readonly templateUrlLataa = signal(false)

  readonly liitetyypit = SOPIMUS_LIITETYYPIT
  liitteet: SopimusLiiteHakutulos[] = []
  readonly liitteetLataa = signal(false)
  readonly liiteStatuses = signal<SopimusLiiteMap<{ fi: LiiteStatus; en: LiiteStatus }>>({})
  readonly liiteUploadStatus = signal<SopimusLiiteMap<LiiteStatus>>({})
  readonly liiteUrls = signal<SopimusLiiteMap<{ fi: SafeResourceUrl; en: SafeResourceUrl }>>({})

  lomake = form(signal<SopimusLomake>({ textfi: '', texten: '' }), polku => {
    required(polku.textfi)
    required(polku.texten)
  })

  clientVariables: string[] = ['{{yrityksen_nimi}}', '{{ytunnus}}', '{{katuosoite}}', '{{postinumero}}', '{{postitoimipaikka}}', '{{maa}}', '{{etunimi}}', '{{sukunimi}}']

  constructor(
    private _firebase: FirebaseLemonator,
    private _dateService: DateService,
    private _dialog: MatDialog,
    private _sanitizer: DomSanitizer,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _uriService: YllapitoUriService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  ngOnInit() {
    this.julkaiseEnabledObservable = this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable

    this._firebase
      .firestoreDoc<Sopimus>(this._uriService.annaSopimuksetMainUri())
      .listen()
      .pipe(takeWhile(resp => !resp, true))
      .subscribe(data => {
        if (!data?.avain) {
          return
        }
        this._nykyinenSopimusAvain = data.avain
        this._lataaTemplateUrlit(data.avain)
      })

    this._lataaLiitteet()

    const sopimuksetObservable = this._firebase.firestoreCollection<Sopimus>(this._uriService.annaSopimuksetVersiotCollection()).listen()

    this.sopimustenVersiotObservable = combineLatest([this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable, sopimuksetObservable]).pipe(
      map(([kirjanpitajienNimitiedotMap, kaikkiVersiot]) => {
        const versiotOrdered = kaikkiVersiot.sort((a, b) => b.pvm.toMillis() - a.pvm.toMillis())

        return versiotOrdered
          .filter(v => !!v)
          .map(versio => {
            const kirjanpitaja = kirjanpitajienNimitiedotMap.get(versio.uid || '')
            const nimi = kirjanpitaja ? `${kirjanpitaja.sukunimi} ${kirjanpitaja.etunimi}` : 'Tuntematon'
            return {
              avain: versio.avain,
              aika: this._dateService.muotoilePaivaJaAikaDate(versio.pvm.toDate(), 'fi'),
              kirjanpitaja: nimi,
              tekstifi: versio.textInDiffLangs?.['fi'] ?? null,
              tekstien: versio.textInDiffLangs?.['en'] ?? null,
            }
          })
      }),
      takeUntil(this._ngUnsubsribe),
    )
  }

  private async _lataaTemplateUrlit(sopimusAvain: string) {
    this.templateUrlFi.set(null)
    this.templateUrlEn.set(null)
    if (this._blobUrlFi) {
      URL.revokeObjectURL(this._blobUrlFi)
      this._blobUrlFi = null
    }
    if (this._blobUrlEn) {
      URL.revokeObjectURL(this._blobUrlEn)
      this._blobUrlEn = null
    }
    this.templateUrlLataa.set(true)
    try {
      const vastaus = await this._firebase.functionsCall<SopimuksenTemplateUrlRequest, SopimuksenTemplateUrlResponse>('annaSopimuksenTemplateUrl', { sopimusAvain })
      if (vastaus?.base64Fi) {
        const blob = new Blob([this._tiedostojenLataamisService.base64ToUint8Array(vastaus.base64Fi)], { type: 'application/pdf' })
        this._blobUrlFi = URL.createObjectURL(blob)
        this.templateUrlFi.set(this._sanitizer.bypassSecurityTrustResourceUrl(this._blobUrlFi))
      }
      if (vastaus?.base64En) {
        const blob = new Blob([this._tiedostojenLataamisService.base64ToUint8Array(vastaus.base64En)], { type: 'application/pdf' })
        this._blobUrlEn = URL.createObjectURL(blob)
        this.templateUrlEn.set(this._sanitizer.bypassSecurityTrustResourceUrl(this._blobUrlEn))
      }
    } catch (err) {
      console.error('Template-URLien haku epäonnistui', err)
    } finally {
      this.templateUrlLataa.set(false)
    }
  }

  private _revokeLiiteBlobUrls() {
    for (const tyyppi of SOPIMUS_LIITETYYPIT) {
      const urls = this._liiteBlobUrls[tyyppi]
      if (urls?.fi) {
        URL.revokeObjectURL(urls.fi)
      }
      if (urls?.en) {
        URL.revokeObjectURL(urls.en)
      }
    }
    this._liiteBlobUrls = {}
  }

  private async _lataaLiitteet() {
    this._revokeLiiteBlobUrls()
    this.liiteUrls.set({})
    this.liitteetLataa.set(true)
    try {
      const vastaus = await this._firebase.functionsCall<void, SopimusLiitteetHakuVastaus>('annaSopimusLiitteetLemonatorille', undefined)
      this.liitteet = vastaus?.liitteet ?? []
      const newUrls: SopimusLiiteMap<{ fi: SafeResourceUrl; en: SafeResourceUrl }> = {}
      const newBlobUrls: SopimusLiiteMap<{ fi: string; en: string }> = {}
      for (const liite of this.liitteet) {
        const urls: { fi: string; en: string } = { fi: null, en: null }
        const safeUrls: { fi: SafeResourceUrl; en: SafeResourceUrl } = { fi: null, en: null }
        if (liite.base64Fi) {
          const blob = new Blob([this._tiedostojenLataamisService.base64ToUint8Array(liite.base64Fi)], { type: 'application/pdf' })
          urls.fi = URL.createObjectURL(blob)
          safeUrls.fi = this._sanitizer.bypassSecurityTrustResourceUrl(urls.fi)
        }
        if (liite.base64En) {
          const blob = new Blob([this._tiedostojenLataamisService.base64ToUint8Array(liite.base64En)], { type: 'application/pdf' })
          urls.en = URL.createObjectURL(blob)
          safeUrls.en = this._sanitizer.bypassSecurityTrustResourceUrl(urls.en)
        }
        newBlobUrls[liite.liitetyyppi] = urls
        newUrls[liite.liitetyyppi] = safeUrls
      }
      this._liiteBlobUrls = newBlobUrls
      this.liiteUrls.set(newUrls)
    } catch (err) {
      console.error('Liitteiden haku epäonnistui', err)
    } finally {
      this.liitteetLataa.set(false)
    }
  }

  async onLiiteFileChange(liitetyyppi: SopimusLiitetyyppi, kieli: TuettuKieli, event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) {
      return
    }
    const file = input.files[0]
    input.value = ''

    const arrayBuffer = await file.arrayBuffer()
    const pdfBase64 = this._tiedostojenLataamisService.uint8ArrayToBase64(new Uint8Array(arrayBuffer))

    this.cachedBase64Map.set({ ...this.cachedBase64Map(), [liitetyyppi]: { ...this.cachedBase64Map()[liitetyyppi], [kieli]: pdfBase64 } })
    const statuses = this.liiteStatuses()[liitetyyppi] ?? { fi: 'idle', en: 'idle' }
    this.liiteStatuses.set({ ...this.liiteStatuses(), [liitetyyppi]: { ...statuses, [kieli]: 'selected' } })
  }

  async paivitaLiite(liitetyyppi: SopimusLiitetyyppi) {
    const pending = this.cachedBase64Map()[liitetyyppi]
    if (!pending?.fi || !pending?.en) {
      return
    }

    this.liiteUploadStatus.set({ ...this.liiteUploadStatus(), [liitetyyppi]: 'uploading' })
    try {
      const pyynto: SopimusLiiteLatausPyynto = { liitetyyppi, pdfBase64Fi: pending.fi, pdfBase64En: pending.en }
      const vastaus = await this._firebase.functionsCall<SopimusLiiteLatausPyynto, SopimusLiiteLatausVastaus>('uploadSopimusLiite', pyynto)
      if (vastaus?.e) {
        this.liiteUploadStatus.set({ ...this.liiteUploadStatus(), [liitetyyppi]: 'error' })
        return
      }
      this.cachedBase64Map.set({ ...this.cachedBase64Map(), [liitetyyppi]: {} })
      this.liiteStatuses.set({ ...this.liiteStatuses(), [liitetyyppi]: { fi: 'idle', en: 'idle' } })
      this.liiteUploadStatus.set({ ...this.liiteUploadStatus(), [liitetyyppi]: 'valid' })
      await this._lataaLiitteet()
    } catch (err) {
      console.error('Liitteen päivitys epäonnistui', err)
      this.liiteUploadStatus.set({ ...this.liiteUploadStatus(), [liitetyyppi]: 'error' })
    }
  }

  julkaise() {
    const dialogData: SopimuksenJulkaiseminenDialogData = {}
    this._dialog
      .open(SopimuksenJulkaiseminenDialog, { data: dialogData })
      .afterClosed()
      .subscribe(() => {
        if (this._nykyinenSopimusAvain) {
          this._lataaTemplateUrlit(this._nykyinenSopimusAvain)
        }
      })
  }

  ngOnDestroy() {
    this._ngUnsubsribe.next()
    if (this._blobUrlFi) {
      URL.revokeObjectURL(this._blobUrlFi)
    }
    if (this._blobUrlEn) {
      URL.revokeObjectURL(this._blobUrlEn)
    }
    this._revokeLiiteBlobUrls()
  }
}
